import React, { useEffect, useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { getStripePromise } from "./stripeclient";
import { createPaymentIntent } from "../../services/paymentservice";

/**
 * Renders Stripe's PaymentElement (Card + PayNow) once a PaymentIntent
 * exists for the given booking. Requires currency "sgd" for PayNow to show.
 */
export default function StripePaymentSection({
  bookingId,
  amount, 
  customerEmail,
  customerName,
  onPaid,
  onError,
}) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!bookingId || !amount) return;
    let cancelled = false;

    createPaymentIntent({
      bookingId,
      amount: Math.round(amount * 100), // cents
      currency: "sgd",
      customerEmail,
      customerName,
    })
      .then((data) => {
        if (!cancelled) setClientSecret(data.clientSecret);
      })
      .catch((err) => {
        console.error("Failed to create payment intent:", err);
        if (!cancelled) setLoadError(err?.message || "Could not load payment options.");
      });

    return () => {
      cancelled = true;
    };
  }, [bookingId, amount, customerEmail, customerName]);

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
        {loadError}
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3">Payment</p>
        <p className="text-sm text-gray-500">Loading payment options…</p>
      </div>
    );
  }

  return (
    <Elements
      stripe={getStripePromise()}
      options={{
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#065f46", // emerald-800, matches the rest of the UI
          },
        },
      }}
    >
      <CheckoutForm onPaid={onPaid} onError={onError} />
    </Elements>
  );
}

function CheckoutForm({ onPaid, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setIsSubmitting(true);
    setError(null);

    // PayNow is redirect-based — confirmPayment will navigate away and
    // back for it. Card payments resolve inline without a redirect.
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message);
      setIsSubmitting(false);
      onError?.(confirmError);
      return;
    }

    // For methods that don't redirect (card), we land here directly.
    // The Firestore booking is only flipped to "paid" by the webhook —
    // this just moves the UI forward optimistically.
    if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
      onPaid?.(paymentIntent);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3">Payment</p>
      <PaymentElement />
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!stripe || isSubmitting}
        className={`mt-4 w-full font-bold py-3 rounded-xl transition-colors text-base ${
          !isSubmitting
            ? "bg-emerald-800 hover:bg-emerald-900 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }`}
      >
        {isSubmitting ? "Processing…" : "Make Payment"}
      </button>
    </div>
  );
}